import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  getGetDashboardSummaryQueryKey,
  getGetOrderStatsQueryKey,
  getGetOrdersQueryKey,
  getOrderPaymentLink,
  useCreateOrder,
  useGetProducts,
} from "@/api";
import type { Product } from "@/api/schemas/product";
import { getApiErrorMessage, isCommissionDebtLimitError } from "@/lib/api/errors";
import { useSessionStore } from "@/stores/session.store";

export type QuickOrderCartItem = {
  product: Product;
  quantity: number;
};

export function useQuickOrder() {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const [searchQuery, setSearchQuery] = useState("");
  const [cartItems, setCartItems] = useState<QuickOrderCartItem[]>([]);
  const [orderLink, setOrderLink] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);

  const { data: productsResponse, isLoading: isProductsLoading } = useGetProducts(
    activeStoreId !== null ? { store_id: activeStoreId, per_page: 100 } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const products = useMemo(
    () => productsResponse?.data?.data ?? [],
    [productsResponse],
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return products;

    return products.filter((product) => product.name.toLowerCase().includes(query));
  }, [products, searchQuery]);

  const totalPrice = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0),
    [cartItems],
  );

  const addProduct = useCallback((product: Product) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }

      return [...prev, { product, quantity: 1 }];
    });
    setOrderLink(null);
    setOrderError(null);
  }, []);

  const removeProduct = useCallback((productId: number) => {
    setCartItems((prev) => prev.filter((item) => item.product.id !== productId));
    setOrderLink(null);
    setOrderError(null);
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    setCartItems((prev) =>
      prev.map((item) => (item.product.id === productId ? { ...item, quantity } : item)),
    );
    setOrderLink(null);
    setOrderError(null);
  }, []);

  const { mutate: createOrderMutate, isPending: isCreatingOrder } = useCreateOrder({
    mutation: {
      onSuccess: async (response) => {
        if (response.status !== 201) return;

        void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() });
        if (activeStoreId !== null) {
          void queryClient.invalidateQueries({
            queryKey: getGetOrderStatsQueryKey({ store_id: activeStoreId }),
          });
          void queryClient.invalidateQueries({
            queryKey: getGetDashboardSummaryQueryKey({ store_id: activeStoreId }),
          });
        }

        const orderId = response.data.data.id;
        try {
          const linkResponse = await getOrderPaymentLink(orderId);
          if (linkResponse.status === 200) {
            setOrderLink(linkResponse.data.data.checkout_url);
            return;
          }
        } catch {
          // Falls through to the message below.
        }
        setOrderError(t("quickOrderErrors.linkFailed"));
      },
      onError: (error) => {
        // The commission debt limit has its own modal (opened by the axios interceptor).
        if (isCommissionDebtLimitError(error)) return;
        setOrderError(getApiErrorMessage(error) ?? t("quickOrderErrors.createFailed"));
      },
    },
  });

  const createOrderLink = useCallback(() => {
    if (activeStoreId === null || cartItems.length === 0) return;

    setOrderError(null);
    createOrderMutate({
      data: {
        // No shipping_address: the customer fills it in on the checkout page.
        store_id: activeStoreId,
        items: cartItems.map((item) => ({
          product_id: item.product.id,
          product_name: item.product.name,
          // Decimal prices come back from the API as strings ("1200000.00").
          unit_price: Number(item.product.price),
          quantity: item.quantity,
        })),
      },
    });
  }, [activeStoreId, cartItems, createOrderMutate]);

  const resetOrder = useCallback(() => {
    setCartItems([]);
    setOrderLink(null);
    setSearchQuery("");
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filteredProducts,
    cartItems,
    totalPrice,
    orderLink,
    orderError,
    isProductsLoading,
    isCreatingOrder,
    addProduct,
    removeProduct,
    updateQuantity,
    createOrderLink,
    resetOrder,
  };
}
